import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  Grid,
  Segment,
  DataTable,
  Table,
  Checkbox,
  ConditionWrapper,
  BaseForm as Form,
  Text,
  Popup,
  Label,
} from 'component/base'

export const Seikyu = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {}
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row =>
        _.find(selectedIds, id => row.kumiaihito_seikyu_id === id)
      )
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      '/api/staff/kumiaiin/kumiaihito_seikyu/list',
      {
        params: { ...condition },
      }
    )
    setLoading(false)
    if (error) return
    setData(data)
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
      />
    ),
    [history, handleChangeSelect]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
          enableSidebar={false}
          compact
        >
          <DataTable
            name='kumiaiin-kumiaihi-seikyu'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeader}
            tableRow={TableRowWithProps}
            data={data}
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

Seikyu.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

Seikyu.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const TableHeader = props => {
  const { withOrder, handleSort, column, direction } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'jotai_kbn' ? direction : null}
        onClick={() => handleSort('jotai_kbn')}
        content='状態'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shime_date' ? direction : null}
        onClick={() => handleSort('shime_date')}
        content='締め年月'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'zaiseki_date' ? direction : null}
        onClick={() => handleSort('zaiseki_date')}
        content='基準在籍日'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'taishosha_su' ? direction : null}
        onClick={() => handleSort('taishosha_su')}
        content='対象者数'
      />
      <Table.HeaderCell
        style={{ width: '13.0rem' }}
        sorted={column === 'taisho_gaku' ? direction : null}
        onClick={() => handleSort('seikyu_gaku')}
        content='対象額計'
      />
      <Table.HeaderCell
        style={{ width: '13.0rem' }}
        sorted={column === 'chosei_gaku' ? direction : null}
        onClick={() => handleSort('chosei_gaku')}
        content='調整額計'
      />
      <Table.HeaderCell
        style={{ width: '13.0rem' }}
        sorted={column === 'seikyu_gaku' ? direction : null}
        onClick={() => handleSort('keshikomi_zumi_gaku')}
        content='請求額計'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'datetime' ? direction : null}
        onClick={() => handleSort('datetime')}
        content='作成日'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'name' ? direction : null}
        onClick={() => handleSort('name')}
        content='担当者'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect } = props || {}

  const {
    kumiaihito_seikyu_id: id,
    shime_date,
    zaiseki_date,
    taishosha_su,
    taisho_gaku,
    chosei_gaku,
    seikyu_gaku,
    datetime,
    name,
    jotai_kbn,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell
        style={{ width: '10.0rem' }}
        content={
          jotai_kbn === 'operated' ? (
            <Popup
              content='調整入力・削除が可能です'
              trigger={<Label content='操作可能' />}
            />
          ) : jotai_kbn === 'closed' ? (
            <Popup
              content='請求締めが行われているので変更するには締めを解除してください'
              trigger={<Label color='orange' content='締め済' />}
            />
          ) : jotai_kbn === 'cleared' ? (
            <Popup
              content='すでに消込が行われているため、操作できません。'
              trigger={<Label color='red' content='消込済み' />}
            />
          ) : (
            <></>
          )
        }
      />
      <Table.Cell style={{ width: '10.0rem' }} content={shime_date} />
      <Table.Cell style={{ width: '10.0rem' }} content={zaiseki_date} />
      <Table.Cell
        style={{
          width: '10.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={taishosha_su} />}
      />
      <Table.Cell
        style={{
          width: '13.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={taisho_gaku} />}
      />
      <Table.Cell
        style={{
          width: '13.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={chosei_gaku} />}
      />
      <Table.Cell
        style={{
          width: '13.0rem',
          textAlign: 'right',
        }}
        content={<Text.Comma content={seikyu_gaku} />}
      />
      <Table.Cell style={{ width: '10.0rem' }} content={datetime} />
      <Table.Cell style={{ width: '10.0rem' }} content={name} />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { nav, condition } = props

  return (
    <>
      <Grid.Column
        style={{
          display: !nav || condition.shime_date_from ? 'block' : 'none',
        }}
      >
        <Form.Textbox.YM
          value={condition.shime_date_from}
          label='締め年月FROM'
          name='shime_date_from'
        />
      </Grid.Column>
      <Grid.Column
        style={{
          display: !nav || condition.shime_date_to ? 'block' : 'none',
        }}
      >
        <Form.Textbox.YM
          value={condition.shime_date_to}
          label='締め年月TO'
          name='shime_date_to'
        />
      </Grid.Column>
    </>
  )
}
